// src/hooks/useGeminiLive.ts
import { useState, useCallback, useRef, useEffect } from 'react'
import { AudioRecorder, AudioPlayer } from '../lib/audio'

interface GeminiLiveConfig {
  systemInstruction: string
  voiceName?: string
}

interface UseGeminiLiveReturn {
  isConnected: boolean
  isListening: boolean
  isSpeaking: boolean
  error: string | null
  connect: (config: GeminiLiveConfig) => Promise<void>
  disconnect: () => void
  startListening: () => void
  stopListening: () => void
  sendText: (text: string) => void
  onResponse?: (text: string) => void
}

export function useGeminiLive(): UseGeminiLiveReturn {
  const [isConnected, setIsConnected] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const recorderRef = useRef<AudioRecorder | null>(null)
  const playerRef = useRef<AudioPlayer | null>(null)
  const onResponseRef = useRef<((text: string) => void) | null>(null)
  const responseTextRef = useRef('')

  useEffect(() => {
    return () => { disconnectInternal() }
  }, [])

  const disconnectInternal = () => {
    recorderRef.current?.stop()
    recorderRef.current = null
    playerRef.current?.stop()
    playerRef.current = null
    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      wsRef.current.onmessage = null
      wsRef.current.close()
      wsRef.current = null
    }
    setIsConnected(false)
    setIsListening(false)
    setIsSpeaking(false)
  }

  const connect = useCallback(async (config: GeminiLiveConfig) => {
    try {
      disconnectInternal()
      setError(null)

      console.log('[GeminiLive] Fetching config...')
      const token = localStorage.getItem('auth_token')
      const res = await fetch('/api/gemini/config', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Kunde inte hämta Gemini-konfiguration')
      const { apiKey, model, wsUrl } = await res.json()
      console.log('[GeminiLive] Got config, model:', model)

      if (!apiKey) throw new Error('Gemini API-nyckel saknas')

      const fullUrl = `${wsUrl}?key=${apiKey}`
      console.log('[GeminiLive] Connecting WebSocket...')
      const ws = new WebSocket(fullUrl)
      wsRef.current = ws

      playerRef.current = new AudioPlayer()

      ws.onopen = () => {
        console.log('[GeminiLive] WebSocket connected, sending setup...')
        // Setup format per official docs
        const setupMsg = {
          setup: {
            model: `models/${model}`,
            generationConfig: {
              responseModalities: ['AUDIO'],
              speechConfig: {
                voiceConfig: {
                  prebuiltVoiceConfig: {
                    voiceName: config.voiceName || 'Fenrir',
                  },
                },
              },
            },
            systemInstruction: {
              parts: [{ text: config.systemInstruction }],
            },
          },
        }
        ws.send(JSON.stringify(setupMsg))
      }

      ws.onmessage = async (event) => {
        try {
          let text: string
          if (event.data instanceof Blob) {
            text = await event.data.text()
          } else {
            text = event.data as string
          }
          const msg = JSON.parse(text)
          handleMessage(msg)
        } catch (e) {
          console.error('[GeminiLive] Parse error:', e)
        }
      }

      ws.onerror = () => {
        setError('Anslutningsfel till röst-AI')
        setIsConnected(false)
      }

      ws.onclose = (event) => {
        console.log('[GeminiLive] WebSocket closed:', event.code, event.reason)
        setIsConnected(false)
        setIsListening(false)
        setIsSpeaking(false)
        if (event.code !== 1000 && event.code !== 1005) {
          setError(`Röstanslutning stängd (${event.code}: ${event.reason || 'okänt'})`)
        }
      }
    } catch (err: any) {
      console.error('[GeminiLive] Connect error:', err)
      setError(err.message || 'Kunde inte ansluta till röst-AI')
    }
  }, [])

  const handleMessage = useCallback((msg: any) => {
    if (msg.setupComplete) {
      console.log('[GeminiLive] Setup complete!')
      setIsConnected(true)
      playerRef.current?.init()

      // Auto-greet and start microphone
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        console.log('[GeminiLive] Sending greeting...')
        wsRef.current.send(JSON.stringify({
          realtimeInput: {
            text: 'Hej Påskharen! Jag vill hjälpa dig hitta dina ägg!'
          }
        }))

        // Auto-start microphone so the child can talk freely
        console.log('[GeminiLive] Auto-starting microphone...')
        const recorder = new AudioRecorder()
        recorder.onData = (base64Chunk) => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              realtimeInput: {
                audio: {
                  data: base64Chunk,
                  mimeType: 'audio/pcm;rate=16000',
                },
              },
            }))
          }
        }
        recorder.start().then(() => {
          recorderRef.current = recorder
          setIsListening(true)
          console.log('[GeminiLive] Microphone auto-started')
        }).catch((err) => {
          console.error('[GeminiLive] Auto-mic failed:', err)
        })
      }
      return
    }

    if (msg.error) {
      console.error('[GeminiLive] Gemini error:', JSON.stringify(msg.error))
      setError(`Gemini: ${msg.error.message || JSON.stringify(msg.error)}`)
      return
    }

    console.log('[GeminiLive] Message keys:', Object.keys(msg).join(', '))

    if (msg.serverContent) {
      const content = msg.serverContent

      if (content.modelTurn?.parts) {
        for (const part of content.modelTurn.parts) {
          if (part.inlineData?.mimeType?.startsWith('audio/')) {
            console.log('[GeminiLive] Audio chunk received, size:', part.inlineData.data?.length)
            setIsSpeaking(true)
            playerRef.current?.playChunk(part.inlineData.data)
          }
          if (part.text) {
            responseTextRef.current += part.text
            console.log('[GeminiLive] Text:', part.text)
          }
        }
      }

      if (content.turnComplete) {
        console.log('[GeminiLive] Turn complete')
        setIsSpeaking(false)
        if (responseTextRef.current && onResponseRef.current) {
          onResponseRef.current(responseTextRef.current)
        }
        responseTextRef.current = ''
      }
    }
  }, [])

  const disconnect = useCallback(() => { disconnectInternal() }, [])

  const startListening = useCallback(async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[GeminiLive] Cannot listen - not connected')
      return
    }

    try {
      playerRef.current?.reset()
      setIsSpeaking(false)

      console.log('[GeminiLive] Starting microphone...')
      const recorder = new AudioRecorder()
      recorder.onData = (base64Chunk) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          // Audio format per official docs
          wsRef.current.send(JSON.stringify({
            realtimeInput: {
              audio: {
                data: base64Chunk,
                mimeType: 'audio/pcm;rate=16000',
              },
            },
          }))
        }
      }
      await recorder.start()
      recorderRef.current = recorder
      setIsListening(true)
      console.log('[GeminiLive] Microphone started')
    } catch (err: any) {
      console.error('[GeminiLive] Mic error:', err)
      setError('Kunde inte starta mikrofonen')
    }
  }, [])

  const stopListening = useCallback(() => {
    console.log('[GeminiLive] Stopping microphone')
    recorderRef.current?.stop()
    recorderRef.current = null
    setIsListening(false)
  }, [])

  const sendText = useCallback((text: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    console.log('[GeminiLive] Sending text:', text)
    // Text input per official docs
    wsRef.current.send(JSON.stringify({
      realtimeInput: { text }
    }))
  }, [])

  return {
    isConnected,
    isListening,
    isSpeaking,
    error,
    connect,
    disconnect,
    startListening,
    stopListening,
    sendText,
    set onResponse(fn: ((text: string) => void) | undefined) {
      onResponseRef.current = fn || null
    },
  }
}
